import React, { useEffect, useState } from 'react';

import { Box } from '@wds/box';
import { Input } from '@wds/input';
import { Grid } from '@wds/grid';
import { IconCheck, IconEye, IconKey } from '@wds/icons';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import { ProgressBar } from '@wds/progress-bar';
import { Popout } from '@wds/popout';
import { Typography } from '@wds/typography';

import PropTypes from 'prop-types';
import STRINGS from './constants';

const PasswordStrengthContent = (props) => {
	const { isLengthOk, isSpecialCharOk, isUpperOk, isNumberOk } = props;
	return (
		<Box my={2}>
			<Grid noBleeds>
				<Grid.Item xs={10}>
					<Typography variant='bodySm'>
						•
						{' '}
						{STRINGS.passwordStrength.length}
					</Typography>
				</Grid.Item>
				<Grid.Item>
					{isLengthOk && <IconCheck color='success' />}
				</Grid.Item>
			</Grid>
			<Grid noBleeds>
				<Grid.Item xs={10}>
					<Typography variant='bodySm'>
						•
						{' '}
						{STRINGS.passwordStrength.upper}
					</Typography>
				</Grid.Item>
				<Grid.Item>
					{isUpperOk && <IconCheck color='success' />}
				</Grid.Item>
			</Grid>
			<Grid noBleeds>
				<Grid.Item xs={10}>
					<Typography variant='bodySm'>
						•
						{' '}
						{STRINGS.passwordStrength.special}
					</Typography>
				</Grid.Item>
				<Grid.Item>
					{isSpecialCharOk && <IconCheck color='success' />}
				</Grid.Item>
			</Grid>
			<Grid noBleeds>
				<Grid.Item xs={10}>
					<Typography variant='bodySm'>
						•
						{' '}
						{STRINGS.passwordStrength.number}
					</Typography>
				</Grid.Item>
				<Grid.Item>
					{isNumberOk && <IconCheck color='success' />}
				</Grid.Item>
			</Grid>
		</Box>
	);
};

const PopoutContent = (props) => {
	const {
		strength, color, isLengthOk, isSpecialCharOk, isUpperOk, isNumberOk,
	} = props;
	return (
		<>
			<Popout.Header>
				{STRINGS.passwordStrength.title}
				:
				<Box
					display='flex'
					justifyContent='center'
					alignItems='center'
					style={{ width: '100%', height: '100%' }}
				>
					<ProgressBar percent={strength} size='sm' color={color} />
				</Box>
			</Popout.Header>
			<Popout.Body>
				{PasswordStrengthContent({ isLengthOk, isSpecialCharOk, isUpperOk, isNumberOk })}
			</Popout.Body>
		</>
	);
};

const PasswordField = (props) => {
	const {
		id, onChange, label, size, placeholder, setIsPasswordValid, usePopout,
	} = props;

	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [strength, setStrength] = useState(0);
	const [color, setColor] = useState('muted');
	const [isLengthOk, setIsLengthOk] = useState(false);
	const [isSpecialCharOk, setIsSpecialCharOk] = useState(false);
	const [isUpperOk, setIsUpperOk] = useState(false);
	const [isNumberOk, setIsNumberOk] = useState(false);
	const [showPopout, setShowPopout] = useState(false);

	const toggleShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleChange = (e) => {
		setPassword(e.target.value);
	};

	// eslint-disable-next-line no-shadow
	const evaluateStrength = (password) => {
		let score = 0;
		const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/g;
		const upperCaseRegex = /[A-Z]/g;
		const numberRegex = /[0-9]/g;
		const specialMatches = password.match(specialCharRegex);
		const upperMatches = password.match(upperCaseRegex);
		const numberMatches = password.match(numberRegex);
		const specialCount = specialMatches ? specialMatches.length : 0;
		const upperCount = upperMatches ? upperMatches.length : 0;
		const numberCount = numberMatches ? numberMatches.length : 0;
		let finalCheckCount = 0;
		if (password.length > 0) { // start validation
			score += 7;
		}
		if (password.length > 7) { // at least 8 characters
			score += 23;
			finalCheckCount += 1;
		}
		if (specialCount > 0) { // count special characters
			score += 15;
			finalCheckCount += 1;
		}
		if (upperCount > 0) { // count uppercase characters
			score += 15;
			finalCheckCount += 1;
		}
		if (numberCount > 0) { // count numbers
			score += 15;
			finalCheckCount += 1;
		}

		if (finalCheckCount === 4 && (specialCount > 2 || upperCount > 2 || numberCount > 2)) {
			// password stronger if multiple special, number or upper
			score += 25;
		}
		setIsLengthOk(password.length > 7);
		setIsSpecialCharOk(specialCount > 0);
		setIsUpperOk(upperCount > 0);
		setIsNumberOk(numberCount > 0);
		setStrength(score);
		setIsPasswordValid(password.length > 7 && specialCount > 0 && upperCount > 0 && numberCount > 0 && score >= 75);
	};

	const getColor = () => {
		// eslint-disable-next-line no-shadow
		let color = 'muted';
		if (strength > 0 && strength < 47) {
			color = 'error';
		}
		if (strength >= 47 && strength <= 75) {
			color = 'upgrade';
		}
		if (strength > 75) {
			color = 'success';
		}
		setColor(color);
	};

	useEffect(() => {
		evaluateStrength(password);
	}, [password]);

	useEffect(() => {
		getColor();
	}, [strength]);

	useEffect(() => {
		if (strength > 75 && setIsPasswordValid) {
			setIsPasswordValid(true);
		}
		onChange({ target: { id, value: password } });
	}, [password]);

	const handleFocus = () => {
		if (!showPopout) {
			setShowPopout(true);
		}
	};

	return (
		<Box
			id='password-container'
			display='flex'
			flexDirection='column'
			style={{ width: '100%' }}
			role='region'
			aria-labelledby='password-label'
		>
			<InputGroup
				label={label}
				stretched
			>
				{usePopout && window.innerWidth > 1000 ? (
					<Popout
						clickable
						show={showPopout}
						size='lg'
						id='password-strength-popout'
						aria-labelledby='password-strength-title'
						trigger={(
							<InputGroupAddon>
								<IconKey
									onClick={toggleShowPassword}
									aria-label='Toggle password visibility'
								/>
							</InputGroupAddon>
						)}
					>
						{PopoutContent({
							strength,
							color,
							isLengthOk,
							isSpecialCharOk,
							isUpperOk,
							isNumberOk,
						})}
					</Popout>
				) : (
					<InputGroupAddon>
						<IconKey
							onClick={toggleShowPassword}
							aria-label='Toggle password visibility'
						/>
					</InputGroupAddon>
				)}
				<Input
					size={size}
					value={password}
					onChange={handleChange}
					id='new_password'
					type={showPassword ? 'text' : 'password'}
					stretched
					onFocus={handleFocus}
					placeholder={placeholder}
					onBlur={() => setShowPopout(false)}
					autoComplete='new-password'
					aria-required='true'
					aria-describedby='password-strength-description'
				/>
				<InputGroupAddon>
					<IconEye
						aria-label={showPassword ? 'Hide password' : 'Show password'}
						onClick={toggleShowPassword}
					/>
				</InputGroupAddon>
			</InputGroup>
			<ProgressBar
				percent={strength}
				size='sm'
				color={color}
				aria-label='Password strength indicator'
			/>
			{(!usePopout || window.innerWidth < 1000) && PasswordStrengthContent({
				isLengthOk,
				isSpecialCharOk,
				isUpperOk,
				isNumberOk,
			})}
		</Box>
	);
};

PasswordStrengthContent.propTypes = {
	isLengthOk: PropTypes.bool.isRequired,
	isSpecialCharOk: PropTypes.bool.isRequired,
	isUpperOk: PropTypes.bool.isRequired,
	isNumberOk: PropTypes.bool.isRequired,
};
PopoutContent.propTypes = {
	strength: PropTypes.number.isRequired,
	color: PropTypes.string.isRequired,
	isLengthOk: PropTypes.bool.isRequired,
	isSpecialCharOk: PropTypes.bool.isRequired,
	isUpperOk: PropTypes.bool.isRequired,
	isNumberOk: PropTypes.bool.isRequired,
};
PasswordField.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	size: PropTypes.string,
	placeholder: PropTypes.string,
	setIsPasswordValid: PropTypes.func,
	usePopout: PropTypes.bool,
};
PasswordField.defaultProps = {
	id: 'password',
	label: null,
	size: 'sm',
	placeholder: null,
	setIsPasswordValid: null,
	usePopout: true,
};
export default PasswordField;
